import { Link } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { ReactNode } from 'react';

import { Page } from '@occc-app/design-system';
import { TableContainer, Button, TableHeader, IPaginationProps } from '@occc/ui-design-system';
import { KioskDefaultScreenFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { DefaultScreensListTable } from './components/default-screens-list-table.component';

import { DefaultScreenDeleteConfirmDialog } from '../../components/default-screen-delete-confirm-dialog.component';
import { routeCreator } from '../../touchpoint.router';
import {
  DEFAULT_SCREENS_ADD_NEW_DEFAULT_SCREEN_BUTTON_TEXT,
  DEFAULT_SCREENS_PAGE_SUBTITLE,
  DEFAULT_SCREENS_PAGE_TITLE,
  DEFAULT_SCREENS_TABLE_TITLE,
} from '../../touchpoint.constants';

type DefaultScreensListPageProps = {
  loading: boolean;
  error?: ApolloError;
  data?: KioskDefaultScreenFragment[];
  paginationProps: IPaginationProps;
  tableActions: ReactNode;
  onItemClick: (defaultScreenId: string) => void;
  onEdit: (defaultScreenId: string) => void;
  onDelete: (defaultScreenId: string) => void;
  defaultScreenNameToDelete: string | null;
  defaultScreenIdToDelete: string | null;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  loadingDeleteKioskDefaultScreen: boolean;
};

export const DefaultScreensListPage = ({
  loading,
  error,
  data,
  paginationProps,
  tableActions,
  onItemClick,
  onEdit,
  onDelete,
  defaultScreenIdToDelete,
  defaultScreenNameToDelete,
  onCloseDeleteDialog,
  onConfirmDelete,
  loadingDeleteKioskDefaultScreen,
}: DefaultScreensListPageProps) => {
  const isUserPermittedToCreateDefaultScreen = useIsUserPermittedTo(
    Feature.TOUCHPOINT_DEFAULT_SCREEN_CREATE,
  );

  const actions = isUserPermittedToCreateDefaultScreen && (
    <Button
      color='secondary'
      variant='contained'
      component={Link}
      to={routeCreator.defaultScreensCreate()}
      role='button'
    >
      {DEFAULT_SCREENS_ADD_NEW_DEFAULT_SCREEN_BUTTON_TEXT}
    </Button>
  );

  return (
    <Page
      browserTabTitle={DEFAULT_SCREENS_PAGE_TITLE}
      actions={actions}
      pageHeaderProps={{
        title: DEFAULT_SCREENS_PAGE_TITLE,
        subtitle: DEFAULT_SCREENS_PAGE_SUBTITLE,
      }}
    >
      <TableContainer>
        <TableHeader title={DEFAULT_SCREENS_TABLE_TITLE} actions={tableActions} />
        <DefaultScreensListTable
          defaultScreens={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          onItemClick={onItemClick}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </TableContainer>

      {defaultScreenIdToDelete && (
        <DefaultScreenDeleteConfirmDialog
          defaultScreenName={defaultScreenNameToDelete}
          onClose={onCloseDeleteDialog}
          onConfirm={onConfirmDelete}
          loading={loadingDeleteKioskDefaultScreen}
        />
      )}
    </Page>
  );
};
